import type { FunctionComponent } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { forTemplate } from '@trello/legacy-i18n';
import { currentLocale } from '@trello/locale';
import { token } from '@trello/theme';

import { LoadingSpinner } from 'app/src/components/LoadingSpinner';
import { usePluginMetricsQuery } from './PluginMetricsQuery.generated';

import styles from './PowerUpMetricsPage.less';

const format = forTemplate('app_management');

export const PowerUpMetricsPage: FunctionComponent<{
  idPlugin: string;
}> = ({ idPlugin }) => {
  const { data, error, loading } = usePluginMetricsQuery({
    variables: {
      idPlugin,
    },
    waitOn: ['None'],
  });

  const plugin = data?.plugin;
  if (error) {
    return <div>{format('something-went-wrong')}</div>;
  }

  if (
    loading ||
    !plugin?.stats ||
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    !plugin.listings ||
    !plugin.listings.length
  ) {
    return <LoadingSpinner />;
  }

  let listing = plugin.listings.find((l) => l.locale === currentLocale);
  if (!listing) {
    listing = plugin.listings[0];
  }
  const name = listing.name;

  const getDateString = (date: Date) =>
    date.toLocaleDateString('default', { month: 'short', day: 'numeric' });

  const getPluginChartData = () => {
    return plugin.stats
      .map((stat) => {
        const date = new Date(stat.date);
        return {
          date: getDateString(date),
          count: stat.boardCount,
        };
      })
      .reverse();
  };

  const getDefaultChartData = () => {
    const today = new Date();
    return [
      {
        date: getDateString(today),
        count: 0,
      },
    ];
  };

  const boardCountChartData: { date: string; count: number }[] = plugin.stats
    .length
    ? getPluginChartData()
    : getDefaultChartData();

  const max = Math.max(...boardCountChartData.map((d) => d.count));
  const min = Math.min(...boardCountChartData.map((d) => d.count));

  return (
    <>
      <div className={styles.header}>
        <h1>{format('metrics')}</h1>
      </div>
      <h3>{format('90-day-board-usage')}</h3>
      <p
        // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml
        dangerouslySetInnerHTML={{
          __html: format('the-number-of-boards-usage', { name }),
        }}
      />
      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          margin={{
            top: 0,
            right: 0,
            bottom: 0,
            left: max >= 1000000 ? 20 : 0,
          }}
          width={800}
          height={400}
          data={boardCountChartData}
        >
          <XAxis dataKey="date" padding={{ left: 8, right: 8 }} />
          <YAxis
            domain={[Math.floor(min * 0.9), Math.floor(max * 1.1)]}
            padding={{ top: 8, bottom: 8 }}
            tickFormatter={(tick) => tick.toLocaleString()}
          />
          <CartesianGrid
            stroke={token('color.border.accent.gray', '#758195')}
            strokeDasharray="3 3"
          />
          <Tooltip
            animationDuration={32}
            formatter={(value) => value.toLocaleString()}
          />
          <Line
            type="monotone"
            dataKey="count"
            stroke={token('color.border.accent.green', '#22A06B')}
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </>
  );
};
